import React, { useEffect, useState } from "react";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorsRange } from "@/features/observability/hooks/frontend-errors-range";
import {
    platformErrorsFilters,
    type PlatformErrorsDimension,
    type PlatformErrorsFilters,
    type PlatformErrorsRangeChoice,
} from "@/features/observability/platform-errors/components/platform-errors-filters";
import { PLATFORM_SEVERITIES, PLATFORM_ENVIRONMENTS, platformErrorsContract } from "@/features/observability/platform-errors/types/platform-errors.types";

type Props = {
    filters: PlatformErrorsFilters;
    range: FrontendErrorsRange | null;
    retentionFloor: number;
    onChange: (next: PlatformErrorsFilters) => void;
};

const RANGE_OPTIONS: { value: PlatformErrorsRangeChoice; label: string }[] = [
    { value: "1h", label: "1 h" },
    { value: "6h", label: "6 h" },
    { value: "24h", label: "24 h" },
    { value: "7d", label: "7 d" },
    { value: "custom", label: "Rango libre" },
];

const ALL = "all";

const pad = (value: number): string => String(value).padStart(2, "0");

const toLocalInput = (ms: number | null): string => {
    if (ms === null) return "";
    const date = new Date(ms);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromLocalInput = (value: string): number | null => {
    if (value === "") return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
};

type DimensionProps = {
    dimension: PlatformErrorsDimension;
    label: string;
    placeholder: string;
    value: string | null;
    onCommit: (value: string | null) => void;
};

// Committed on blur or Enter, not per keystroke: every commit is a new query.
function DimensionInput({ dimension, label, placeholder, value, onCommit }: DimensionProps) {
    const [draft, setDraft] = useState(value === null ? "" : value);

    useEffect(() => {
        setDraft(value === null ? "" : value);
    }, [value]);

    const commit = (): void => {
        const next = platformErrorsFilters.toDimension(dimension, draft);
        if (next !== value) onCommit(next);
    };

    return (
        <label className="fe-field">
            {label}
            <input
                className="fe-input"
                value={draft}
                placeholder={placeholder}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === "Enter") commit();
                }}
            />
        </label>
    );
}

export function PlatformErrorsFilterBar({ filters, range, retentionFloor, onChange }: Props) {
    const choose = (choice: PlatformErrorsRangeChoice): void => {
        if (choice === "custom") {
            onChange({
                ...filters,
                range: "custom",
                customFrom: range === null ? filters.customFrom : range.from,
                customTo: range === null ? filters.customTo : range.to,
            });
            return;
        }
        onChange({ ...filters, range: choice, customFrom: null, customTo: null });
    };

    return (
        <div className="fe-filters">
            <label className="fe-field">
                Entorno
                <select
                    className="fe-select"
                    value={filters.environment === null ? ALL : filters.environment}
                    onChange={(event) => {
                        const value = event.target.value;
                        onChange({ ...filters, environment: platformErrorsContract.isEnvironment(value) ? value : null });
                    }}
                >
                    <option value={ALL}>Del despliegue</option>
                    {PLATFORM_ENVIRONMENTS.map((environment) => (
                        <option key={environment} value={environment}>{environment}</option>
                    ))}
                </select>
            </label>

            <div className="fe-field">
                Rango temporal
                <div className="fe-field__row">
                    <div className="fe-seg" role="group" aria-label="Rango temporal">
                        {RANGE_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className="fe-seg__btn"
                                aria-pressed={filters.range === option.value}
                                onClick={() => choose(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    {filters.range === "custom" ? (
                        <div className="fe-field__row">
                            <input
                                type="datetime-local"
                                className="fe-input fe-input--date"
                                aria-label="Desde"
                                min={toLocalInput(retentionFloor)}
                                value={toLocalInput(filters.customFrom)}
                                onChange={(event) => onChange({ ...filters, customFrom: fromLocalInput(event.target.value) })}
                            />
                            <span className="fe-muted">→</span>
                            <input
                                type="datetime-local"
                                className="fe-input fe-input--date"
                                aria-label="Hasta"
                                min={toLocalInput(retentionFloor)}
                                value={toLocalInput(filters.customTo)}
                                onChange={(event) => onChange({ ...filters, customTo: fromLocalInput(event.target.value) })}
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            <label className="fe-field">
                Severidad
                <select
                    className="fe-select"
                    value={filters.severity === null ? ALL : filters.severity}
                    onChange={(event) => {
                        const value = event.target.value;
                        onChange({ ...filters, severity: platformErrorsContract.isSeverity(value) ? value : null });
                    }}
                >
                    <option value={ALL}>Todas</option>
                    {PLATFORM_SEVERITIES.map((severity) => (
                        <option key={severity} value={severity}>{severity}</option>
                    ))}
                </select>
            </label>

            <label className="fe-field">
                Categoría
                <select
                    className="fe-select fe-select--wide"
                    value={filters.category === null ? ALL : filters.category}
                    onChange={(event) => {
                        const value = event.target.value;
                        onChange({ ...filters, category: frontendErrorsFormat.isCategory(value) ? value : null });
                    }}
                >
                    <option value={ALL}>Todas</option>
                    {frontendErrorsFormat.CATEGORIES.map((category) => (
                        <option key={category} value={category}>{frontendErrorsFormat.categoryStyle(category).label}</option>
                    ))}
                </select>
            </label>

            <DimensionInput dimension="service" label="Servicio" placeholder="payoutworker" value={filters.service} onCommit={(service) => onChange({ ...filters, service })} />
            <DimensionInput
                dimension="resourceType"
                label="Tipo de recurso"
                placeholder="cloud_run_revision"
                value={filters.resourceType}
                onCommit={(resourceType) => onChange({ ...filters, resourceType })}
            />
            <DimensionInput dimension="errorCode" label="Código de error" placeholder="UNKNOWN_RUNTIME" value={filters.errorCode} onCommit={(errorCode) => onChange({ ...filters, errorCode })} />
        </div>
    );
}
